import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { DBDef_DiseaseProfile } from '@app/dp-shared';
import { resolveEntity } from '@nu-art/thunderstorm-frontend';
import { ModuleFE_DiseaseProfile, ModuleFE_DiseaseValue, ModuleFE_DiseaseView, ModuleFE_MultiVar } from '../../_entity.js';
import { createSearchIntegration, Dialog_Confirmation } from '@app/styles';
import { exists, StaticLogger, voidFunction } from '@nu-art/ts-common';
import { Manager_DiseaseProfile_MultiVarTab_Values, Manager_DiseaseProfile_ProfileTab_General } from '../../ui/_managers/Manager_DiseaseProfile/types.js';
import { Manager_DiseaseProfile } from '../../ui/_managers/Manager_DiseaseProfile/Manager_DiseaseProfile.js';
import { SearchAddOn_ByTag } from '@app/hcs-frontend/search/index';
import { Dialog_ViewConfigEditor } from '../../ui/dialogs/Dialog_ViewConfigEditor/Dialog_ViewConfigEditor.js';
import { Toaster } from '@nu-art/toasting';
import { Dialog_NewDiseaseProfile } from '../../ui/_managers/Manager_DiseaseProfile/renderers/Dialog_NewDiseaseProfile/Dialog_NewDiseaseProfile.js';
import { Renderer_SearchResult, SearchResult_BaseRightClickActions } from '@app/styles/frontend/ui/components/Renderer_SearchResult/Renderer_SearchResult';
import { AccessLevel } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { displayEntityDependencies } from '@app/styles/frontend/ui/consts';
import { getDiseaseProfileTimestamps } from '../../utils/disease-profile-timestamps.js';
const resolveDP = resolveEntity(ModuleFE_DiseaseProfile);
const toaster = new Toaster('whi__disease-profile');
const getPermissions = () => ({
    edit: ModuleFE_DiseaseProfile.permissions.collection.edit.getAccessLevel() === AccessLevel.HasAccess,
    delete: ModuleFE_DiseaseProfile.permissions.collection.delete.getAccessLevel() === AccessLevel.HasAccess,
});
const { WorkHubItem, UtilObject, SearchItem } = createSearchIntegration({
    module: ModuleFE_DiseaseProfile,
    dbDef: DBDef_DiseaseProfile,
    tag: ['Dp', 'Disease Profile'],
    modulesToAwait: [ModuleFE_DiseaseProfile, ModuleFE_MultiVar, ModuleFE_DiseaseValue, ModuleFE_DiseaseView],
    ManagerComponent: Manager_DiseaseProfile,
    customOpenEditor: (item) => {
        const id = resolveDP(item)?._id;
        if (!id)
            return;
        const diseaseView = ModuleFE_DiseaseView.cache.all()[0];
        WorkHubItem.openTab(UtilObject.generateTabId(id), UtilObject.renderLabel(item) ?? 'New Disease Profile', {
            itemId: id,
            editMode: false,
            profile: {
                selectedTab: Manager_DiseaseProfile_ProfileTab_General,
                selectedDiseaseViewId: diseaseView?._id,
            },
            multiVar: {
                selectedTab: Manager_DiseaseProfile_MultiVarTab_Values,
            },
        });
    },
    customMenuResolver: async (tab, workHubItem) => {
        const diseaseProfile = resolveDP(tab.renderArgs.itemId);
        if (!diseaseProfile)
            return [];
        const permissions = getPermissions();
        const selectedDiseaseView = ModuleFE_DiseaseView.cache.unique(tab.renderArgs.profile.selectedDiseaseViewId);
        const sections = [
            {
                actions: [
                    {
                        label: `${tab.renderArgs.editMode ? 'View' : 'Edit'} Mode`,
                        action: () => workHubItem.updateArgs(tab.id, { editMode: !tab.renderArgs.editMode }),
                        visible: !tab.renderArgs.readonly,
                    },
                ]
            },
            {
                actions: [
                    {
                        label: 'Copy ID',
                        action: () => UtilObject.copyId(tab.renderArgs.itemId),
                    },
                    {
                        label: 'Copy Item',
                        action: () => UtilObject.copyObj(diseaseProfile),
                    },
                    {
                        label: 'Export JSON',
                        action: () => UtilObject.exportJSON(diseaseProfile),
                    }
                ]
            },
            {
                actions: [
                    {
                        label: 'Delete Profile',
                        visible: permissions.delete,
                        action: () => {
                            Dialog_Confirmation.show({
                                header: 'Are you sure?',
                                content: () => _jsxs(_Fragment, { children: [_jsx("div", { children: "You're about to delete profile" }), _jsx("div", { children: diseaseProfile.label })] }),
                                buttons: [
                                    {
                                        label: 'Yes, Delete',
                                        isAsync: true,
                                        action: async () => {
                                            try {
                                                await ModuleFE_DiseaseProfile._v1.deleteWholeDiseaseProfile({ diseaseProfileId: diseaseProfile._id }).executeSync();
                                                workHubItem.closeTab(tab.id);
                                                toaster.toastSuccess({ title: 'Disease Profile Delete', body: 'Disease profile deleted successfully' });
                                            }
                                            catch (err) {
                                                StaticLogger.logError('WorkHubItem_DiseaseProfile', err);
                                                toaster.toastError({ title: 'Disease Profile Delete', body: `Disease profile deletion failed:\n${err.message}` });
                                            }
                                        }
                                    },
                                    {
                                        label: 'Don\'t Delete',
                                        isPreferred: true,
                                        action: voidFunction
                                    }
                                ]
                            });
                        }
                    }
                ],
            },
            {
                label: 'Disease View',
                actions: [
                    {
                        label: 'Add New View',
                        visible: permissions.edit,
                        action: () => Dialog_ViewConfigEditor.show(),
                    },
                    {
                        label: `Edit "${selectedDiseaseView?.label}" View`,
                        visible: permissions.edit && exists(selectedDiseaseView),
                        action: () => Dialog_ViewConfigEditor.show(selectedDiseaseView),
                    },
                    {
                        label: `Delete "${selectedDiseaseView?.label}" View`,
                        visible: permissions.delete && exists(selectedDiseaseView),
                        action: () => {
                            Dialog_Confirmation.show({
                                header: 'Are you sure?',
                                content: () => _jsxs(_Fragment, { children: [_jsx("div", { children: "You're about to disease view" }), _jsx("div", { children: selectedDiseaseView.label })] }),
                                buttons: [
                                    {
                                        label: 'Yes, Delete',
                                        isAsync: true,
                                        action: async () => {
                                            try {
                                                await ModuleFE_DiseaseView.v1.delete(selectedDiseaseView).executeSync();
                                                const nextView = ModuleFE_DiseaseView.cache.all()[0];
                                                workHubItem.updateArgs(tab.id, { profile: { ...tab.renderArgs.profile, selectedDiseaseViewId: nextView?._id } });
                                                toaster.toastSuccess({ title: 'Disease View Delete', body: 'Disease view deleted successfully' });
                                            }
                                            catch (err) {
                                                StaticLogger.logError('WorkHubItem_DiseaseProfile', err);
                                                toaster.toastError({ title: 'Disease View Delete', body: `Disease view deletion failed:\n${err.message}` });
                                            }
                                        }
                                    },
                                    {
                                        label: 'Don\'t Delete',
                                        isPreferred: true,
                                        action: voidFunction,
                                    }
                                ]
                            });
                        }
                    }
                ],
            }
        ];
        return sections;
    },
    extraAddOnKeys: [SearchAddOn_ByTag.key],
    extraSearchAddOns: {
        [SearchAddOn_ByTag.methodName]: (result) => resolveDP(result.id)?.tagIds ?? [],
    },
    resultRenderer: (result, style) => {
        const dp = resolveDP(result.id);
        return _jsx(Renderer_SearchResult, { entity: dp, entityLabel: 'Disease Profile', style: style, itemLabel: UtilObject.renderLabel(dp) ?? 'N/A', timestamps: getDiseaseProfileTimestamps(result.id), onClickAction: () => UtilObject_DiseaseProfile.openEditor(dp), rightClickActions: [
                ...SearchResult_BaseRightClickActions(dp),
                { label: 'Check Usage', action: () => displayEntityDependencies(result.dbKey, result.id) },
            ] });
    }
});
export const WorkHubItem_DiseaseProfile = WorkHubItem;
export const SearchItem_DiseaseProfile = SearchItem;
export const UtilObject_DiseaseProfile = {
    ...UtilObject,
    openNew: () => {
        Dialog_NewDiseaseProfile.show(dp => UtilObject_DiseaseProfile.openEditor(dp));
    },
    openEditor: (item) => {
        const id = typeof item === 'string' ? item : item?._id;
        if (!id)
            return;
        const diseaseView = ModuleFE_DiseaseView.cache.all()[0];
        WorkHubItem.openTab(UtilObject.generateTabId(id), UtilObject.renderLabel(id) ?? 'New Disease Profile', {
            itemId: id,
            editMode: false,
            profile: {
                selectedTab: Manager_DiseaseProfile_ProfileTab_General,
                selectedDiseaseViewId: diseaseView?._id,
            },
            multiVar: {
                selectedTab: Manager_DiseaseProfile_MultiVarTab_Values,
            },
        });
    },
};
//# sourceMappingURL=disease-profile.js.map